/*
  Rogue redesign (Sep 2026): places the collection guide tiles and the hero
  guide link from sections/rh-collection-tiles.liquid.

  - A tile goes into #product-grid as an li.grid__item, before the product at
    its position (a separate position on phones, up to 749px).
  - Filters, sort and the layout switcher (assets/facets.js) replace the grid,
    and "Show more" (assets/infinite-scroll.js) adds products to it. A
    MutationObserver on #ProductGridContainer puts the tiles back.
  - Loop guard: placing a tile changes the grid too. The script moves a tile
    only when it is not already in the right place, drops its own mutation
    records, and runs at most once for each animation frame.
  - A tile that spans 2 columns (6 per row on desktop, 4 per row on a
    tablet) never starts in the last column. It moves one product later, so
    the row before it stays full.
  The theme files above are not changed.
*/
(() => {
  const root = document.querySelector('[data-rh-col-tiles]');
  if (!root) return;

  const phone = window.matchMedia('(max-width: 749px)');
  const tablet = window.matchMedia('(min-width: 750px) and (max-width: 989px)');

  // Columns in the grid and the columns a tile spans (see assets/rh-collection.css).
  const layout = (grid) => {
    if (phone.matches) return { cols: 1, span: 1 };
    if (tablet.matches) return grid.classList.contains('grid--4-col-tablet') ? { cols: 4, span: 2 } : { cols: 3, span: 1 };
    return grid.classList.contains('grid--6-col-desktop') ? { cols: 6, span: 2 } : { cols: 4, span: 1 };
  };
  const tiles = [...root.querySelectorAll('template[data-rh-tile]')]
    .map((template) => ({
      id: template.dataset.rhTile,
      template,
      desktop: parseInt(template.dataset.position, 10) || 4,
      mobile: parseInt(template.dataset.positionMobile, 10) || parseInt(template.dataset.position, 10) || 5,
    }));

  const heroTemplate = root.querySelector('template[data-rh-hero-link]');
  const heroSlot = document.querySelector('[data-rh-hero-slot]');
  if (heroTemplate && heroSlot && !heroSlot.firstElementChild) {
    heroSlot.appendChild(heroTemplate.content.cloneNode(true));
    heroSlot.hidden = false;
  }

  if (!tiles.length) return;

  let observer = null;
  let queued = false;

  const place = () => {
    queued = false;
    const grid = document.getElementById('product-grid');
    if (!grid || grid.tagName !== 'UL') return;

    const key = phone.matches ? 'mobile' : 'desktop';
    const { cols, span } = layout(grid);
    const ordered = [...tiles].sort((a, b) => a[key] - b[key]);

    for (const tile of ordered) {
      let item = grid.querySelector(`[data-rh-tile-id="${tile.id}"]`);
      const others = [...grid.children].filter((el) => el !== item);
      const products = others.filter((el) => !el.hasAttribute('data-rh-tile-id'));
      // Not enough products to reach the position: leave the tile out.
      if (products.length < tile[key] - 1 || products.length === 0) {
        if (item) item.remove();
        continue;
      }
      if (!item) item = tile.template.content.firstElementChild.cloneNode(true);
      // Index among all children, so that earlier tiles count.
      let index = tile[key] - 1;
      if (span > 1) {
        const used = others.slice(0, index).reduce((n, el) => n + (el.hasAttribute('data-rh-tile-id') ? span : 1), 0);
        if (used % cols > cols - span) index += cols - (used % cols);
      }
      const before = others[index] || null;
      if (item.parentNode !== grid || item.nextElementSibling !== before) {
        grid.insertBefore(item, before);
      }
    }
    if (observer) observer.takeRecords();
  };

  const queue = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(place);
  };

  place();

  const container = document.getElementById('ProductGridContainer');
  if (container) {
    observer = new MutationObserver((records) => {
      // Only a change to the grid itself counts: not changes inside a card,
      // and not the tiles moving.
      const relevant = records.some((record) => {
        const target = record.target;
        // The layout switcher changes the grid classes: the tile may need to move.
        if (record.type === 'attributes') return target.id === 'product-grid';
        if (target !== container && target.id !== 'product-grid' && !target.classList?.contains('collection')) return false;
        return [...record.addedNodes, ...record.removedNodes].some(
          (node) => node.nodeType === 1 && !node.hasAttribute('data-rh-tile-id')
        );
      });
      if (relevant) queue();
    });
    observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  for (const query of [phone, tablet]) {
    if (query.addEventListener) query.addEventListener('change', queue);
    else if (query.addListener) query.addListener(queue);
  }
})();
